import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Play,
  Heart,
  Trophy,
  RotateCcw,
  Pause,
  Home,
  Volume2,
  VolumeX,
} from "lucide-react";

const NumberRainGame = ({
  speak,
  setScore,
  setGameActive,
  timeLeft,
  setTimeLeft,
}) => {
  // Sample number data with more vibrant colors
  const numbers = [
    { letter: "1", color: "#FF6B6B", emoji: "🔴" },
    { letter: "2", color: "#4ECDC4", emoji: "🟢" },
    { letter: "3", color: "#45B7D1", emoji: "🔵" },
    { letter: "4", color: "#96CEB4", emoji: "🟡" },
    { letter: "5", color: "#FFEAA7", emoji: "🟠" },
    { letter: "6", color: "#DDA0DD", emoji: "🟣" },
    { letter: "7", color: "#98D8C8", emoji: "🟤" },
    { letter: "8", color: "#F7DC6F", emoji: "⚪" },
    { letter: "9", color: "#BB8FCE", emoji: "🟡" },
    { letter: "10", color: "#85C1E9", emoji: "🔵" },
  ];

  // Sound effects
  const soundEffects = {
    correct: new Audio(
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA"
    ),
    wrong: new Audio(
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA"
    ),
    levelUp: new Audio(
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA"
    ),
    gameOver: new Audio(
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA"
    ),
  };

  const [fallingNumbers, setFallingNumbers] = useState([]);
  const [targetNumber, setTargetNumber] = useState(null);
  const [rainScore, setRainScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [localGameActive, setLocalGameActive] = useState(false);
  const [level, setLevel] = useState(1);
  const [targetsCaught, setTargetsCaught] = useState(0);
  const [targetsNeeded, setTargetsNeeded] = useState(5);
  const [isPortrait, setIsPortrait] = useState(
    window.innerHeight > window.innerWidth
  );
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [floatingEmojis, setFloatingEmojis] = useState([]);

  const spawnTimerRef = useRef(null);
  const moveTimerRef = useRef(null);
  const levelTimeoutRef = useRef(null);
  const gameAreaRef = useRef(null);
  const confettiTimerRef = useRef(null);
  const lastSpokenRef = useRef("");

  // Use refs to store current values to avoid stale closures
  const gameStateRef = useRef({
    level: 1,
    rainScore: 0,
    targetNumber: null,
    localGameActive: false,
    lives: 3,
    isMuted: false,
    isPaused: false,
  });

  // Handle orientation changes
  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update refs whenever state changes
  useEffect(() => {
    gameStateRef.current = {
      level,
      rainScore,
      targetNumber,
      localGameActive,
      lives,
      isMuted,
      isPaused,
    };
  }, [
    level,
    rainScore,
    targetNumber,
    localGameActive,
    lives,
    isMuted,
    isPaused,
  ]);

  // Improved speak function with anti-echo
  const defaultSpeak = useCallback(
    (text) => {
      if (gameStateRef.current.isMuted) return;

      // Prevent repeating the same message too quickly
      if (lastSpokenRef.current === text) return;
      lastSpokenRef.current = text;

      // Clear the last spoken after a delay
      setTimeout(() => {
        lastSpokenRef.current = "";
      }, 2000);

      console.log(`Speech: ${text}`);
      if (typeof speak === "function") {
        speak(text);
      } else if ("speechSynthesis" in window) {
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1.2;
        utterance.volume = 0.8;

        // Use a child-friendly voice if available
        const voices = window.speechSynthesis.getVoices();
        const childVoice = voices.find(
          (voice) =>
            voice.name.includes("Child") ||
            voice.name.includes("Kids") ||
            voice.name.includes("Google UK English Female")
        );

        if (childVoice) {
          utterance.voice = childVoice;
        }

        window.speechSynthesis.speak(utterance);
      }
    },
    [speak]
  );

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (spawnTimerRef.current) {
      clearInterval(spawnTimerRef.current);
      spawnTimerRef.current = null;
    }
    if (moveTimerRef.current) {
      clearInterval(moveTimerRef.current);
      moveTimerRef.current = null;
    }
    if (levelTimeoutRef.current) {
      clearTimeout(levelTimeoutRef.current);
      levelTimeoutRef.current = null;
    }
    if (confettiTimerRef.current) {
      clearTimeout(confettiTimerRef.current);
      confettiTimerRef.current = null;
    }
  }, []);

  // Initialize target number when component mounts
  useEffect(() => {
    if (numbers && numbers.length > 0 && !targetNumber) {
      const randomTarget = numbers[Math.floor(Math.random() * numbers.length)];
      setTargetNumber(randomTarget);
    }
  }, [numbers, targetNumber]);

  // Play sound effect
  const playSound = useCallback((soundName) => {
    if (gameStateRef.current.isMuted) return;

    try {
      // In a real app, you would use actual sound files
      // For this example, we'll just log the sound
      console.log(`Playing sound: ${soundName}`);
    } catch (error) {
      console.error("Error playing sound:", error);
    }
  }, []);

  // Show confetti animation
  const showConfettiAnimation = useCallback(() => {
    setShowConfetti(true);
    confettiTimerRef.current = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);
  }, []);

  // Add floating emoji effect
  const addFloatingEmoji = useCallback((emoji, x, y) => {
    const id = Date.now() + Math.random();
    const newEmoji = {
      id,
      emoji,
      x,
      y,
      scale: 1,
    };

    setFloatingEmojis((prev) => [...prev, newEmoji]);

    // Remove after animation
    setTimeout(() => {
      setFloatingEmojis((prev) => prev.filter((e) => e.id !== id));
    }, 1000);
  }, []);

  const resetRainGame = useCallback(() => {
    clearAllTimers();

    setRainScore(0);
    setLives(3);
    setLevel(1);
    setTargetsCaught(0);
    setTargetsNeeded(5);
    setFallingNumbers([]);
    setLocalGameActive(false);
    setIsPaused(false);
    setShowConfetti(false);
    setFloatingEmojis([]);

    if (typeof setGameActive === "function") {
      setGameActive(false);
    }

    // Set new target number
    if (numbers && numbers.length > 0) {
      const randomTarget = numbers[Math.floor(Math.random() * numbers.length)];
      setTargetNumber(randomTarget);
    }
  }, [clearAllTimers, setGameActive, numbers]);

  const endGame = useCallback(
    (finalLevel, finalScore) => {
      clearAllTimers();
      setLocalGameActive(false);
      setIsPaused(false);

      if (typeof setGameActive === "function") {
        setGameActive(false);
      }

      playSound("gameOver");
      defaultSpeak(
        `Game Over! You reached level ${finalLevel}. Final score: ${finalScore}`
      );
    },
    [clearAllTimers, setGameActive, defaultSpeak, playSound]
  );

  const nextLevel = useCallback(() => {
    // Check if game is still active
    if (!gameStateRef.current.localGameActive) return;

    const newLevel = gameStateRef.current.level + 1;
    const newTargetsNeeded = targetsNeeded + 2;

    setLevel(newLevel);
    setTargetsCaught(0);
    setTargetsNeeded(newTargetsNeeded);
    setLives((prev) => Math.min(3, prev + 1));

    // Choose new target number
    const randomTarget = numbers[Math.floor(Math.random() * numbers.length)];
    setTargetNumber(randomTarget);

    // Clear existing numbers
    setFallingNumbers([]);

    // Show celebration
    showConfettiAnimation();
    playSound("levelUp");

    defaultSpeak(
      `Level ${newLevel}! Catch ${newTargetsNeeded} times, ${randomTarget.letter} numbers!`
    );
  }, [targetsNeeded, numbers, defaultSpeak, playSound, showConfettiAnimation]);

  // FIXED: Proper startGameTimers function
  const startGameTimers = useCallback(() => {
    // Clear any existing timers first
    clearAllTimers();

    // Spawn numbers
    spawnTimerRef.current = setInterval(() => {
      const gameState = gameStateRef.current;
      if (
        !gameState.localGameActive ||
        !gameState.targetNumber ||
        gameState.isPaused
      )
        return;

      // Higher chance of target numbers in early levels
      const targetChance = Math.max(0.3, 0.6 - gameState.level * 0.05);
      const shouldSpawnTarget = Math.random() < targetChance;

      let numberToSpawn;
      let isTargetNumber;

      if (shouldSpawnTarget) {
        numberToSpawn = gameState.targetNumber;
        isTargetNumber = true;
      } else {
        // Spawn a random non-target number
        const nonTargetNumbers = numbers.filter(
          (n) => n.letter !== gameState.targetNumber.letter
        );
        numberToSpawn =
          nonTargetNumbers[Math.floor(Math.random() * nonTargetNumbers.length)];
        isTargetNumber = false;
      }

      if (!numberToSpawn) return;

      const newNumber = {
        id: Date.now() + Math.random(),
        letter: numberToSpawn.letter,
        color: numberToSpawn.color,
        emoji: numberToSpawn.emoji,
        x: Math.random() * 80 + 10,
        y: 0,
        speed: Math.random() * 2 + 1 + gameState.level * 0.5,
        isTarget: isTargetNumber,
        rotation: Math.random() * 20 - 10,
        scale: 0.8 + Math.random() * 0.4,
      };

      setFallingNumbers((prev) => [...prev, newNumber]);
    }, Math.max(500, 1000 - level * 50));

    // Move numbers down
    moveTimerRef.current = setInterval(() => {
      const gameState = gameStateRef.current;
      if (gameState.isPaused) return;

      setFallingNumbers((prev) => {
        const updated = prev.map((number) => ({
          ...number,
          y: number.y + number.speed,
          rotation: number.rotation + (number.isTarget ? 0.5 : 0.3),
          scale: number.isTarget
            ? 0.9 + 0.1 * Math.sin(Date.now() / 200)
            : number.scale,
        }));

        const gameAreaHeight = gameAreaRef.current?.clientHeight || 350;
        const filtered = updated.filter((number) => {
          if (number.y > gameAreaHeight) {
            if (number.isTarget) {
              const currentGameState = gameStateRef.current;
              const newLives = Math.max(0, currentGameState.lives - 1);

              setLives(newLives);

              if (newLives === 0) {
                endGame(currentGameState.level, currentGameState.rainScore);
              }
            }
            return false;
          }
          return true;
        });

        return filtered;
      });
    }, 50);
  }, [level, numbers, endGame, clearAllTimers]); // FIXED: Removed isPaused dependency

  // FIXED: Proper togglePause function
  const togglePause = useCallback(() => {
    if (!localGameActive) return;

    setIsPaused((prevPaused) => {
      const newPaused = !prevPaused;

      if (newPaused) {
        // Pausing the game
        clearAllTimers();
        defaultSpeak("Game paused");
      } else {
        // Resuming the game
        startGameTimers();
        defaultSpeak("Game resumed");
      }

      return newPaused;
    });
  }, [localGameActive, clearAllTimers, defaultSpeak, startGameTimers]); // FIXED: Added proper dependencies

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      if (!newMuted) {
        defaultSpeak("Sound on");
      } else {
        defaultSpeak("Sound off");
      }
      return newMuted;
    });
  }, [defaultSpeak]);

  const startRainGame = useCallback(() => {
    resetRainGame();
    setLocalGameActive(true);
    setIsPaused(false); // FIXED: Ensure game starts unpaused

    if (typeof setGameActive === "function") {
      setGameActive(true);
    }

    // Make sure we have a target number set
    let currentTarget = targetNumber;
    if (!currentTarget && numbers && numbers.length > 0) {
      currentTarget = numbers[Math.floor(Math.random() * numbers.length)];
      setTargetNumber(currentTarget);
    }

    if (currentTarget) {
      defaultSpeak(`Level 1! Catch 5 times ${currentTarget.letter} number!`);
    }

    startGameTimers();
  }, [
    resetRainGame,
    setGameActive,
    targetNumber,
    numbers,
    defaultSpeak,
    startGameTimers,
  ]);

  const catchNumber = useCallback(
    (numberId, x, y) => {
      // FIXED: Check if game is paused
      if (isPaused) return;

      const fallingNumber = fallingNumbers.find((n) => n.id === numberId);
      if (!fallingNumber) return;

      const currentGameState = gameStateRef.current;
      const isCorrectTarget =
        fallingNumber.isTarget &&
        currentGameState.targetNumber &&
        fallingNumber.letter === currentGameState.targetNumber.letter;

      if (isCorrectTarget) {
        const points = 10 * currentGameState.level;
        const newScore = currentGameState.rainScore + points;

        setRainScore(newScore);

        if (typeof setScore === "function") {
          setScore((prevScore) => prevScore + points);
        }

        // Add floating emoji effect
        addFloatingEmoji("⭐", x, y);
        playSound("correct");

        setTargetsCaught((prev) => {
          const newCount = prev + 1;
          if (newCount >= targetsNeeded) {
            defaultSpeak(`Level ${currentGameState.level} complete! Amazing!`);
            levelTimeoutRef.current = setTimeout(() => {
              if (
                gameStateRef.current.localGameActive &&
                !gameStateRef.current.isPaused
              ) {
                nextLevel();
              }
            }, 1500);
          } else {
            defaultSpeak("Great catch!");
          }
          return newCount;
        });
      } else {
        const newLives = Math.max(0, currentGameState.lives - 1);
        setLives(newLives);

        // Add floating emoji effect
        addFloatingEmoji("💔", x, y);
        playSound("wrong");

        if (newLives === 0) {
          endGame(currentGameState.level, currentGameState.rainScore);
        } else {
          defaultSpeak("Wrong number!");
        }
      }

      setFallingNumbers((prev) => prev.filter((n) => n.id !== numberId));
    },
    [
      fallingNumbers,
      targetsNeeded,
      setScore,
      defaultSpeak,
      nextLevel,
      endGame,
      playSound,
      addFloatingEmoji,
      isPaused, // FIXED: Added isPaused dependency
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);

  // Stop timers when game becomes inactive or paused
  useEffect(() => {
    if (!localGameActive || isPaused) {
      clearAllTimers();
    }
  }, [localGameActive, isPaused, clearAllTimers]);

  // Calculate responsive values
  const gameAreaHeight = isPortrait ? "58vh" : "60vh";
  const targetNumberSize = isPortrait ? "2.5rem" : "3.5rem";
  const numberSize = isPortrait ? "1.8rem" : "2.5rem";

  return (
    <div className="number-rain-game mobile-optimized">
      <link
        href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.2/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Confetti effect */}
      {showConfetti && (
        <div className="confetti-overlay">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
              }}
            />
          ))}
        </div>
      )}

      {/* Compact Header for Mobile */}
      <div className="game-header mb-3">
        <h2 className="game-title h4 fw-bold text-secondary mb-2">
          Number Rain Adventure
        </h2>

        {/* Target Number & Lives - Compact Row */}
        <div className="target-lives-container d-flex justify-content-between align-items-center mb-3">
          <div className="target-section d-flex align-items-center">
            <span className="catch-label me-2 fw-medium">Catch:</span>
            {targetNumber ? (
              <div
                className="target-number fw-bold rounded bg-white border-3 shadow-sm d-flex align-items-center justify-content-center"
                style={{
                  color: targetNumber.color,
                  borderColor: targetNumber.color,
                  borderWidth: "3px",
                  width: targetNumberSize,
                  height: targetNumberSize,
                  fontSize: isPortrait ? "1.5rem" : "2rem",
                  animation: isPaused
                    ? "none"
                    : "pulse 1.5s infinite, bounce 2s infinite", // FIXED: Pause animation when game is paused
                }}
              >
                {targetNumber.letter}
              </div>
            ) : (
              <div
                className="target-number fw-bold rounded bg-light border-3 d-flex align-items-center justify-content-center"
                style={{
                  borderColor: "#dee2e6",
                  borderWidth: "3px",
                  width: targetNumberSize,
                  height: targetNumberSize,
                  fontSize: isPortrait ? "1.5rem" : "2rem",
                }}
              >
                ?
              </div>
            )}
          </div>

          {/* Lives */}
          <div className="lives-section d-flex align-items-center">
            <span className="me-2 fw-medium">Lives:</span>
            <div className="d-flex align-items-center">
              {Array.from({ length: 3 }, (_, i) => (
                <Heart
                  key={i}
                  className={i < lives ? "text-danger" : "text-muted"}
                  style={{
                    width: isPortrait ? "18px" : "22px",
                    height: isPortrait ? "18px" : "22px",
                    fill: i < lives ? "currentColor" : "none",
                    animation:
                      i < lives && !isPaused ? "beat 1s infinite" : "none", // FIXED: Pause animation when game is paused
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Stats - Compact Grid */}
        <div className="stats-grid row g-2 mb-2 my--80">
          <div className="col-4">
            <div className="stat-item text-center px-2 py-1 rounded border bg-light">
              <Trophy
                className="d-block mx-auto mb-1"
                style={{ width: "16px", height: "16px", color: "#a855f7" }}
              />
              <div className="stat-value fw-bold fs-6">Lvl {level}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="stat-item text-center px-2 py-1 rounded border bg-light">
              <div className="stat-label small">Progress</div>
              <div className="stat-value fw-bold fs-6">
                {targetsCaught}/{targetsNeeded}
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="stat-item text-center px-2 py-1 rounded border bg-light">
              <div className="stat-label small">Score</div>
              <div className="stat-value fw-bold fs-6">{rainScore}</div>
            </div>
          </div>
        </div>

        {/* FIXED: Pause indicator */}
        {isPaused && localGameActive && (
          <div className="paused-indicator text-center mb-2">
            <div className="badge bg-warning text-dark fs-6 p-2">
              ⏸️ GAME PAUSED
            </div>
          </div>
        )}
      </div>

      {!localGameActive ? (
        <div className="game-start-screen text-center">
          <button
            onClick={startRainGame}
            className="start-btn btn btn-lg mb-3 d-inline-flex align-items-center justify-content-center shadow-sm w-100 py-3"
            style={{
              background: "linear-gradient(to right, #10b981, #059669)",
              color: "white",
              fontSize: isPortrait ? "1.1rem" : "1.25rem",
              border: "none",
              borderRadius: "12px",
              animation: "pulse 2s infinite",
            }}
          >
            <Play className="me-2" style={{ width: "20px", height: "20px" }} />
            Start Adventure
          </button>

          {lives === 0 && (
            <div
              className="game-over-message fs-6 fw-bold p-2 rounded border mb-3"
              style={{
                color: "#dc2626",
                backgroundColor: "#fef2f2",
                borderColor: "#fecaca",
              }}
            >
              Game Over! Level {level} • Score: {rainScore}
            </div>
          )}

          <div className="game-instructions small text-muted p-2 rounded bg-light">
            <div className="row g-1 text-center">
              <div className="col-12 mb-1 fw-medium">How to Play:</div>
              <div className="col-4">🎯 Tap correct numbers</div>
              <div className="col-4">🚀 Level up to advance</div>
              <div className="col-4">❤️ Avoid wrong numbers</div>
            </div>
            {level >= 5 && (
              <div
                className="mt-2 fw-medium text-center"
                style={{ color: "#7c3aed", fontSize: "0.8rem" }}
              >
                {level < 10 && "🔄 Numbers fall faster!"}
                {level >= 10 && level < 15 && "🌀 More numbers to catch!"}
                {level >= 15 && level < 20 && "⚡ Lightning speed!"}
                {level >= 20 && "🌪️ Maximum difficulty!"}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="game-active-area">
          {targetsCaught >= targetsNeeded && (
            <div
              className="level-complete-message mb-3 p-2 rounded border text-center"
              style={{
                background: "linear-gradient(to right, #fef3cd, #fed7aa)",
                borderColor: "#fde047",
                animation: isPaused ? "none" : "bounce 0.5s infinite alternate", // FIXED: Pause animation when game is paused
              }}
            >
              <div
                className="fw-bold"
                style={{
                  color: "#ea580c",
                  fontSize: isPortrait ? "0.9rem" : "1.1rem",
                }}
              >
                🎉 Level {level} Complete! 🎉
              </div>
            </div>
          )}

          {/* Game Area */}
          <div
            ref={gameAreaRef}
            className="game-area position-relative rounded border overflow-hidden mx-auto"
            style={{
              height: gameAreaHeight,
              background: "linear-gradient(to bottom, #bfdbfe, #bbf7d0)",
              borderColor: "#3b82f6",
              borderWidth: "3px",
              maxWidth: "100%",
              touchAction: "none",
              marginTop: "-20px",
              opacity: isPaused ? 0.7 : 1, // FIXED: Visual indicator when paused
              transition: "opacity 0.3s ease",
            }}
          >
            {fallingNumbers.map((number) => (
              <div
                key={number.id}
                onClick={(e) => {
                  if (isPaused) return; // FIXED: Prevent catching numbers when paused
                  const rect = e.target.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  catchNumber(number.id, x, y);
                }}
                className="falling-number position-absolute fw-bold user-select-none"
                style={{
                  left: `${number.x}%`,
                  top: `${number.y}px`,
                  color: number.color,
                  fontSize: numberSize,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  transform: `rotate(${number.rotation}deg) scale(${number.scale})`,
                  zIndex: 10,
                  animation: isPaused
                    ? "none"
                    : number.isTarget // FIXED: Pause animations when game is paused
                    ? "glow 1s infinite alternate, float 3s infinite ease-in-out"
                    : "float 3s infinite ease-in-out",
                  cursor: isPaused ? "default" : "pointer", // FIXED: Change cursor when paused
                  WebkitTapHighlightColor: "transparent",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  transition: "transform 0.1s ease",
                }}
              >
                {number.letter}
              </div>
            ))}

            {/* Floating emojis */}
            {floatingEmojis.map((emoji) => (
              <div
                key={emoji.id}
                className="floating-emoji position-absolute"
                style={{
                  left: `${emoji.x}px`,
                  top: `${emoji.y}px`,
                  fontSize: "2rem",
                  animation: isPaused ? "none" : "floatUp 1s ease-out forwards", // FIXED: Pause animation when game is paused
                  zIndex: 20,
                }}
              >
                {emoji.emoji}
              </div>
            ))}

            {/* Background decorative elements */}
            <div
              className="position-absolute"
              style={{
                top: "0.5rem",
                left: "0.5rem",
                fontSize: "1.5rem",
                opacity: 0.2,
                animation: isPaused ? "none" : "float 4s infinite ease-in-out", // FIXED: Pause animation when game is paused
              }}
            >
              ☁️
            </div>
            <div
              className="position-absolute"
              style={{
                top: "1rem",
                right: "1rem",
                fontSize: "1.5rem",
                opacity: 0.2,
                animation: isPaused ? "none" : "float 5s infinite ease-in-out", // FIXED: Pause animation when game is paused
              }}
            >
              ☁️
            </div>
            <div
              className="position-absolute"
              style={{
                bottom: "1rem",
                left: "2rem",
                fontSize: "1.5rem",
                opacity: 0.2,
                animation: isPaused ? "none" : "float 6s infinite ease-in-out", // FIXED: Pause animation when game is paused
              }}
            >
              🌿
            </div>
            <div
              className="position-absolute"
              style={{
                bottom: "2rem",
                right: "1rem",
                fontSize: "1.5rem",
                opacity: 0.2,
                animation: isPaused ? "none" : "float 7s infinite ease-in-out", // FIXED: Pause animation when game is paused
              }}
            >
              🌿
            </div>

            {/* FIXED: Pause overlay */}
            {isPaused && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  zIndex: 30,
                }}
              >
                <div className="text-white fw-bold fs-4 text-center">
                  ⏸️ PAUSED
                  <br />
                  <small className="fs-6">Tap Resume to continue</small>
                </div>
              </div>
            )}
          </div>

          {/* Game Controls */}
          <div className="game-controls mt-2 d-flex justify-content-center gap-2">
            <button
              onClick={toggleMute}
              className="btn btn-outline-secondary btn-sm d-flex align-items-center"
              style={{ fontSize: isPortrait ? "0.8rem" : "0.9rem" }}
            >
              {isMuted ? (
                <VolumeX size={14} className="me-1" />
              ) : (
                <Volume2 size={14} className="me-1" />
              )}
              Sound
            </button>

            <button
              onClick={togglePause}
              className="btn btn-outline-warning btn-sm d-flex align-items-center"
              style={{ fontSize: isPortrait ? "0.8rem" : "0.9rem" }}
            >
              {isPaused ? (
                <>
                  <Play size={14} className="me-1" />
                  Resume
                </>
              ) : (
                <>
                  <Pause size={14} className="me-1" />
                  Pause
                </>
              )}
            </button>

            <button
              onClick={resetRainGame}
              className="btn btn-outline-danger btn-sm d-flex align-items-center"
              style={{ fontSize: isPortrait ? "0.8rem" : "0.9rem" }}
            >
              <RotateCcw size={14} className="me-1" />
              Restart
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          .number-rain-game.mobile-optimized {
            padding: 0.5rem;
            max-width: 100%;
            overflow-x: hidden;
          }
          
          .game-header {
            padding: 0.5rem;
          }
          
          .game-title {
          margin-top:-20px !important;
            font-size: 1.25rem;
            text-align: center;
          }
          
          .target-lives-container {
            max-width: 100%;
          }
          
          .stats-grid .stat-item {
            margin-top: -10px;
            font-size: 0.8rem;
          }
          
          .stat-value {
            line-height: 1.2;
          }
          
          .game-area {
            min-height: 250px;
          }
          
          .falling-number {
            transition: transform 0.1s ease;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
          }
          
          .falling-number:active {
            transform: scale(1.3) !important;
          }
          
          .confetti-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 100;
          }
          
          .confetti {
            position: absolute;
            width: 10px;
            height: 10px;
            animation: confettiFall 3s linear forwards;
          }
          
          .floating-emoji {
            pointer-events: none;
          }
          
          .paused-indicator {
            animation: pulse 1.5s infinite;
          }
          
          @keyframes bounce {
            from { transform: translateY(0px); }
            to { transform: translateY(-10px); }
          }
          
          @keyframes beat {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          
          @keyframes glow {
            from { 
              text-shadow: 2px 2px 4px rgba(0,0,0,0.5), 0 0 10px currentColor; 
            }
            to { 
              text-shadow: 2px 2px 4px rgba(0,0,0,0.5), 0 0 20px currentColor; 
            }
          }

          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(5deg); }
          }
          
          @keyframes floatUp {
            0% { 
              transform: translateY(0) scale(1);
              opacity: 1;
            }
            100% { 
              transform: translateY(-50px) scale(0.5);
              opacity: 0;
            }
          }
          
          @keyframes confettiFall {
            0% {
              transform: translateY(-100px) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(360deg);
              opacity: 0;
            }
          }
          
          /* Responsive adjustments for larger screens */
          @media (min-width: 768px) {
            .number-rain-game.mobile-optimized {
              padding: 1rem;
            }
            
            .game-title {
              font-size: 1.5rem;
            }
            
            .target-number {
              font-size: 2.5rem !important;
              width: 4rem !important;
              height: 4rem !important;
            }
            
            .stats-grid .stat-item {
              font-size: 0.9rem;
            }
          }
          
          /* Landscape orientation optimizations */
          @media (max-height: 600px) and (orientation: landscape) {
            .game-header {
              padding: 0.25rem;
              margin-bottom: 0.5rem;
            }
            
            .game-title {
              font-size: 1.1rem;
              margin-bottom: 0.25rem;
            }
            
            .target-lives-container {
              margin-bottom: 0.5rem;
            }
            
            .stats-grid {
              margin-bottom: 0.5rem;
            }
            
            .game-area {
              height: 45vh !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default NumberRainGame;
